import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

/**
 * Height of a form control. Data-entry screens run dense; everything else has room.
 *
 * Three values rather than the four Button carries. The masters supplied all three: most run their
 * form fields at h-9 and their toolbars at h-8, and the cost centre screen runs tighter still at
 * h-7. What was not deliberate is that each page wrote its own copy of the style to get there.
 *
 * `sm` is the default for Input, Select and Combobox. h-9 read too tall on a screen that is nothing
 * but fields; reach for `md` only where a control stands on its own.
 */
export type ControlSize = 'xs' | 'sm' | 'md';

export const controlSize: Record<ControlSize, string> = {
  xs: 'h-7 text-xs',
  sm: 'h-8 text-sm',
  md: 'h-9 text-sm',
};

/**
 * The border, ground, focus ring and disabled treatment every text-entry control shares.
 *
 * One definition. Five master pages each carried a private copy of this string and they had
 * drifted — different disabled grounds, one missing the dark-mode ring — which is what made the
 * screens look like they came from different systems.
 */
export const controlBase = (hasError?: boolean) =>
  cn(
    'w-full rounded-md border bg-white dark:bg-gray-800',
    'text-gray-900 dark:text-gray-100',
    'placeholder:text-gray-400 dark:placeholder:text-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
    'disabled:bg-gray-50 dark:disabled:bg-gray-900/40 disabled:text-gray-400 disabled:cursor-not-allowed',
    'transition-colors',
    hasError
      ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
      : 'border-gray-300 dark:border-gray-600',
  );

interface FieldProps {
  label?: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Label above a control, error or hint below it.
 *
 * Input, Select and Textarea all wrap themselves in this rather than repeating the markup, so a
 * validation message sits in the same place and reads the same weight whichever control raised it.
 * An error hides the hint: showing both puts the advice you already failed to follow directly under
 * the reason you failed.
 */
export const Field = ({ label, htmlFor, error, hint, children, className }: FieldProps) => {
  // Nothing to label and nothing to say: hand the control back bare. A wrapper forcing w-full
  // would break the controls that sit inline in a toolbar at a fixed width, and those are the ones
  // most likely to be written raw to avoid the wrapper in the first place.
  if (!label && !error && !hint && !className) return <>{children}</>;

  return (
  <div className={cn('w-full', className)}>
    {label && (
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        {label}
      </label>
    )}
    {children}
    {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    {hint && !error && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </div>
  );
};
